/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        components/mobile/CompactZoneGrid.tsx
 * @description Grille compacte des zones pour l'ecran mobile sans scrolling
 *              Affiche les 6 zones dans une grille 2x3 ou 3x2
 *
 * @version     2.4
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { zoneService } from '@/services/zoneService';
import type { DeneignementZone, StatutZone } from '@/types';
import {
  CheckCircleIcon,
  PlayIcon,
  ClockIcon,
} from '@heroicons/react/24/solid';
import {
  CheckCircleIcon as CheckCircleOutline,
} from '@heroicons/react/24/outline';

interface CompactZoneGridProps {
  deneignementId: number;
  zones: DeneignementZone[];
  progression: number;
  onZoneUpdate?: () => void;
  readOnly?: boolean;
  zoneDemandee?: string;
}

// Mapping des zones prioritaires
const ZONE_PRIORITE_MAPPING: Record<string, number[]> = {
  complete: [1, 2, 3, 4, 5, 6],
  avion: [3, 5, 6],
  voies: [2, 4],
  equipements: [1, 3],
};

export function CompactZoneGrid({
  deneignementId,
  zones,
  progression,
  onZoneUpdate,
  readOnly = false,
  zoneDemandee,
}: CompactZoneGridProps) {
  const queryClient = useQueryClient();
  const [activeZone, setActiveZone] = useState<number | null>(null);
  const [commentaire, setCommentaire] = useState('');

  const zonesPrioritaires = zoneDemandee ? ZONE_PRIORITE_MAPPING[zoneDemandee] || [] : [];
  const isZonePrioritaire = (num: number) => zonesPrioritaires.includes(num);

  // Mutations
  const demarrerMutation = useMutation({
    mutationFn: (zoneId: number) => zoneService.demarrerZone(deneignementId, zoneId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement-zones', deneignementId] });
      onZoneUpdate?.();
    },
  });

  const terminerMutation = useMutation({
    mutationFn: ({ zoneId, commentaire }: { zoneId: number; commentaire?: string }) =>
      zoneService.terminerZone(deneignementId, zoneId, commentaire),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement-zones', deneignementId] });
      setCommentaire('');
      setActiveZone(null);
      onZoneUpdate?.();
    },
  });

  const handleAction = (zone: DeneignementZone) => {
    if (readOnly) return;

    if (zone.statut === 'a_faire') {
      demarrerMutation.mutate(zone.zone.id);
    } else if (zone.statut === 'en_cours') {
      setActiveZone(zone.id);
    }
  };

  const handleTerminer = () => {
    if (!activeZone) return;
    const zone = zones.find((z) => z.id === activeZone);
    if (zone) {
      terminerMutation.mutate({ zoneId: zone.zone.id, commentaire: commentaire || undefined });
    }
  };

  const isPending = demarrerMutation.isPending || terminerMutation.isPending;

  const getZoneStyle = (zone: DeneignementZone) => {
    const isPrio = isZonePrioritaire(zone.zone.numero);
    switch (zone.statut) {
      case 'termine':
        return 'bg-green-500 text-white border-green-600';
      case 'en_cours':
        return 'bg-blue-500 text-white border-blue-600 animate-pulse';
      default:
        return isPrio
          ? 'bg-amber-50 text-gray-700 border-amber-300 border-2'
          : 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getIcon = (statut: StatutZone) => {
    switch (statut) {
      case 'termine':
        return <CheckCircleIcon className="w-5 h-5" />;
      case 'en_cours':
        return <ClockIcon className="w-5 h-5" />;
      default:
        return <CheckCircleOutline className="w-5 h-5 opacity-40" />;
    }
  };

  // Compter les zones
  const completed = zones.filter((z) => z.statut === 'termine').length;
  const inProgress = zones.filter((z) => z.statut === 'en_cours').length;

  return (
    <div className="space-y-3">
      {/* Barre de progression compacte */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-300"
            style={{ width: `${progression}%` }}
          />
        </div>
        <span className="text-sm font-bold text-primary-600 min-w-[3rem] text-right">
          {progression.toFixed(0)}%
        </span>
      </div>

      {/* Stats rapides */}
      <div className="flex justify-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          {completed} OK
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
          {inProgress} en cours
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-gray-300" />
          {zones.length - completed - inProgress} restant
        </span>
      </div>

      {/* Grille des zones 3x2 */}
      <div className="grid grid-cols-3 gap-2">
        {zones.map((zone) => (
          <button
            key={zone.id}
            onClick={() => handleAction(zone)}
            disabled={isPending || readOnly || zone.statut === 'termine'}
            className={clsx(
              'relative flex flex-col items-center justify-center p-3 rounded-xl border transition-all min-h-[72px]',
              getZoneStyle(zone),
              !readOnly && zone.statut !== 'termine' && 'active:scale-95'
            )}
          >
            {/* Numero de zone */}
            <span className="text-lg font-bold">{zone.zone.numero}</span>

            {/* Icon statut */}
            <div className="mt-1">{getIcon(zone.statut)}</div>

            {/* Indicateur prioritaire */}
            {isZonePrioritaire(zone.zone.numero) && zone.statut === 'a_faire' && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center text-[10px] text-white font-bold">
                !
              </span>
            )}

            {/* Label action */}
            {zone.statut === 'a_faire' && !readOnly && (
              <span className="text-[10px] mt-0.5 opacity-70">Tap = Start</span>
            )}
            {zone.statut === 'en_cours' && !readOnly && (
              <span className="text-[10px] mt-0.5">Tap = Fin</span>
            )}
          </button>
        ))}
      </div>

      {/* Modal terminer zone (compact) */}
      {activeZone && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
          <div className="bg-white w-full max-w-lg rounded-t-2xl p-4 safe-area-inset-bottom animate-slide-up">
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />

            <h3 className="text-lg font-bold text-center mb-3">
              Terminer Zone {zones.find((z) => z.id === activeZone)?.zone.numero}
            </h3>

            <textarea
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              placeholder="Commentaire optionnel..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3"
              rows={2}
            />

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setActiveZone(null);
                  setCommentaire('');
                }}
                className="flex-1 py-3 px-4 rounded-xl border border-gray-300 text-gray-700 font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleTerminer}
                disabled={terminerMutation.isPending}
                className="flex-1 py-3 px-4 rounded-xl bg-green-500 text-white font-bold flex items-center justify-center gap-2"
              >
                <CheckCircleIcon className="w-5 h-5" />
                Terminer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompactZoneGrid;
